import React from 'react';
import { pick } from 'ramda';
import { Form } from 'js/components/common/Form';
import { Button } from 'js/components/common/Button';
import { Input } from 'js/components/common/Fields/Input';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { componentService } from 'js/services/component.service';
import { withComponent } from 'js/components/hocs/withComponent';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as hooks from './hooks';
import * as Types from './types';
import { BANK_ID_LOGIN, BANK_ID_REGISTRATION } from './constants';
import { Captcha } from './components/Captcha';
import { Preloader } from './components/Preloader';
import { useDynamicReducerSelector, withDynamicReducer } from './store';

import style from './BankIDAuth.scss';

export const BankIDAuthBare: React.FC<Types.IBankIDAuthProps> = ({ replacerType = 'login' }) => {
    const onSubmit = hooks.useOnSubmit(replacerType);
    const translate = useComponentTranslations();
    const validationConfig = hooks.useValidationConfig();
    const {
        formError,
        isRecaptchaShown,
        isExternalScriptLoaded,
        isWaitingForBankIdConfirmation
    } = useDynamicReducerSelector(pick(['formError', 'isExternalScriptLoaded', 'isWaitingForBankIdConfirmation', 'isRecaptchaShown']));

    hooks.useGetExternalScript();

    if (!isExternalScriptLoaded) {
        return null;
    }

    if (isWaitingForBankIdConfirmation) {
        return <Preloader />;
    }

    return (
        <Form<Types.IBankIDAuthFormValues>
            onSubmit={onSubmit}
            validateOnBlur={true}
            validationConfig={validationConfig}
            render={({ handleSubmit, submitting }) => (
                <form className={style.form} onSubmit={(event) => event.preventDefault()}>
                    {formError && (
                        <ErrorMessage
                            classNames={{ error: style.formError }}
                            error={formError}
                            animate={true}
                        />
                    )}

                    <Input
                        type="text"
                        name="personalId"
                        classNames={{ control: style.control }}
                        label={translate('playtech.mobile.system.bankIDAuth.personalID.label')}
                        placeholder={translate('playtech.mobile.system.bankIDAuth.personalID.placeholder')}
                        aria-label={translate([
                            'playtech.mobile.system.bankIDAuth.personalID.label',
                            'playtech.mobile.system.bankIDAuth.personalID.placeholder',
                            'playtech.mobile.system.input.aria-label'
                        ])}
                    />

                    <Captcha />

                    <div className={style.submitButtonContainer}>
                        <Button type="button" className={style.submitButton} disabled={submitting || isRecaptchaShown} onClick={handleSubmit}>
                            {translate(`playtech.mobile.system.bankIDAuth.${replacerType}.submit`)}
                        </Button>
                    </div>
                </form>
            )}
        />
    );
};

export const BankIDLoginBare: React.FC = () => <BankIDAuthBare replacerType='login' />;
export const BankIDRegistrationBare: React.FC = () => <BankIDAuthBare replacerType='registration' />;

export const BankIDAuth = withComponent()(withDynamicReducer()(BankIDAuthBare));
export const BankIDLoin = withComponent()(withDynamicReducer()(BankIDLoginBare));
export const BankIDRegistration = withComponent()(withDynamicReducer()(BankIDRegistrationBare));

componentService.registerWebContentComponents({ [BANK_ID_LOGIN]: BankIDLoin });
componentService.registerWebContentComponents({ [BANK_ID_REGISTRATION]: BankIDRegistration });
